import React, { Suspense, useEffect, useState } from 'react';
import {
  Alert, Button, Card, Descriptions, Empty, Popover, Skeleton, Space, Tag, Table, Tooltip, Typography, message,
} from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ReloadOutlined, ToolOutlined, WarningOutlined, HourglassOutlined, CloudSyncOutlined,
  DisconnectOutlined, SearchOutlined, CloseCircleOutlined, CheckCircleOutlined, HistoryOutlined,
  LockOutlined, ThunderboltOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { AnimatePresence, motion } from 'framer-motion';
import NodeInfoBar from '../DetailPanel/NodeInfoBar';
import SuggestedActionBanner from '../DetailPanel/SuggestedActionBanner';
import InfoCards from '../DetailPanel/InfoCards';
import NodeLifeTimeline from '../DetailPanel/NodeLifeTimeline';
import FirmwareHistoryTable from '../DetailPanel/FirmwareHistoryTable';
import LogsSection from '../DetailPanel/LogsSection';
import TaskSection from '../DetailPanel/TaskSection';
import TaskTimeline from './TaskTimeline';
import { PENDING_CLASS_TAG, classTagMap, classTooltipMap, priorityColor, priorityLabel, subclassTagMap, shortActor, isClaimFresh } from '../triageMaps';
import { parseClassifierNotes, deriveEvidence } from '../../utils/fallbackHints';
import { TRIAGE_ACTIONS, TICKET_CLASS_IDS } from '../../types/triage';
import type {
  Issue,
  IssueAlert,
  Task,
  Ticket,
  TriageActionDef,
  NodeDetail,
  BatteryStatus,
  Gateway,
  NodeHistoryEvent,
  FirmwareEvent,
  NodeRecovery,
} from '../../types/triage';
import { triageColors } from '../../theme/antd-theme';
import { matchRule } from './classifierRules';
import { fetchAgentIssuePrediction, fetchIssueCohort, type AgentIssuePrediction, type CohortResponse } from '../../config/api';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import TriageHistory from './TriageHistory';

dayjs.extend(relativeTime);

// Lazy load with an inline retry CTA on chunk failure (e.g. a deploy invalidated
// the previous build's chunk hash). Previously we hard-reloaded the page on
// failure, which trashed in-flight triage state and any unsaved comment text.
const InvestigationDashboard = React.lazy(() =>
  import('../DetailPanel/InvestigationDashboard').catch((err) => {
    console.warn('[InvestigationDashboard] chunk failed to load:', err);
    return {
      default: () => (
        <Alert
          type="warning"
          showIcon
          message="Charts failed to load"
          description="The investigation chart bundle could not be downloaded. This usually means the page has been updated since you opened it."
          action={
            <Button size="small" onClick={() => window.location.reload()}>Reload page</Button>
          }
        />
      ),
    } as any;
  })
);

const actionIcons: Record<string, React.ReactNode> = {
  battery: <ThunderboltOutlined />,
  hardware: <ToolOutlined />,
  tampering: <WarningOutlined />,
  hardware_eol: <HourglassOutlined />,
  firmware: <CloudSyncOutlined />,
  out_of_range: <DisconnectOutlined />,
  on_site: <SearchOutlined />,
  false_alert: <CloseCircleOutlined />,
  close: <CheckCircleOutlined />,
};

const actionButtonStyle: Record<string, any> = {
  // 2026-05-25 audit: `danger: true` rendered Hardware as red — operators
  // pattern-match red as destructive, but Hardware is a common legitimate
  // outcome. Aligning with the rest of the bar (neutral border + per-class
  // accent color from triageColors).
  hardware: { style: { borderColor: triageColors.actionHardware, color: triageColors.actionHardware } },
  tampering: { style: { borderColor: triageColors.actionTampering, color: triageColors.actionTampering } },
  hardware_eol: { style: { borderColor: triageColors.actionHardwareEol, color: triageColors.actionHardwareEol } },
  firmware: { type: 'primary' as const, ghost: true },
  out_of_range: { style: { borderColor: triageColors.actionOutOfRange, color: triageColors.actionOutOfRange } },
  on_site: {},
  false_alert: {},
  close: { style: { borderColor: triageColors.actionClose, color: triageColors.actionClose } },
};

function confidenceBucket(c: number | null): { text: string; color: string } | null {
  if (c == null) return null;
  if (c >= 80) return { text: 'hi', color: '#389e0d' };
  if (c >= 50) return { text: 'med', color: '#d48806' };
  return { text: 'lo', color: '#cf1322' };
}

// Synthesizes a Ticket-shaped object from an Issue so existing detail subcomponents
// (NodeInfoBar, InfoCards, SuggestedActionBanner, TaskSection) can be reused.
// The negative ticket_id is a sentinel: it will never match a real row.
function issueToPseudoTicket(issue: Issue): Ticket {
  return {
    ticket_id: -issue.issue_id,
    ticket_created: issue.created_at,
    // MUST be the alert's own re-detection time, not issue.updated_at.
    // updated_at only moves when the sweeper reclassifies, so it read as
    // "Last Detected: 12 days ago" on nodes whose alert had re-fired minutes
    // earlier — 94.6% of the open queue was showing a 7-30d-stale value.
    // Falls back to updated_at only when no open alert remains.
    last_detected: issue.last_alert_detected ?? issue.updated_at,
    priority: issue.priority ?? 3,
    observable_id: issue.observable_id,
    observable_type: issue.observable_type,
    location_id: issue.location_id,
    ticket_class_id: issue.ticket_class_id ?? 0,
    ticket_status_id: issue.status_id,
    action_id: issue.action_id,
    root_cause_id: issue.root_cause_id,
    comment: issue.comment,
    ticket_class: issue.ticket_class ?? 'Unclassified',
    status_name: issue.status_name ?? '',
    action_name: issue.action_name,
    root_cause_name: issue.root_cause_name,
    location_name: issue.location_name,
    observable_name: issue.observable_name,
    autoset_name: issue.autoset_name,
    key_name: issue.key_name,
    observable_info: issue.observable_info,
    position_type: null,
    key_id: null,
    // Same 10-minute heartbeat rule the ticket board uses. Deliberately a weak
    // display-only hint: the authoritative "node is back" judgement is
    // analytics-backed and lives server-side in the sweeper's recovery close,
    // because nodes.last_seen is join-polluted (invariant #5). Previously
    // hardcoded false, which was simply wrong for ~11% of the queue.
    is_online: issue.node_last_seen
      ? Date.now() - new Date(issue.node_last_seen).getTime() < 10 * 60 * 1000
      : false,
    past_ticket_count: 0,
    cohort_size: 1,
  };
}


const ClassifierSection: React.FC<{ issue: Issue }> = ({ issue }) => {
  const matched = matchRule(issue.classifier_notes);
  const isManual = issue.classifier_version === 'manual';

  // Just the essentials: which rule fired, the class it points to, and what it
  // checks for. The prose rationale already lives in the "Why this class"
  // banner above. The classifier version and the full decision-flow ladder
  // were developer-oriented detail and are dropped here to keep the operator
  // view scannable (the ladder still lives in the codebase / labeling guide).
  return (
    <Card size="small" styles={{ body: { padding: 12 } }}>
      <Descriptions size="small" column={1} colon={false}
        labelStyle={{ color: '#8c8c8c', fontSize: 11 }}
        contentStyle={{ fontSize: 12 }}
      >
        <Descriptions.Item label="Rule matched">
          {isManual ? (
            <Tag color="purple" style={{ margin: 0 }}>manual override</Tag>
          ) : matched ? (
            <Tag color="blue" style={{ margin: 0 }}>{matched.id}</Tag>
          ) : (
            <Tag style={{ margin: 0 }}>unknown</Tag>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Class">
          {/* issue.ticket_class is the real, persisted final class. matched.className
              is only a static mirror keyed off the base rule name and goes stale
              whenever an escalation (e.g. aged/multi-property -> Hardware EOL)
              changes the final class without changing which rule's note prefix
              matched — surface that with an "escalated" tag rather than silently
              showing the wrong class (observed 2026-07-12 on R-GW-DIRECT). */}
          {issue.ticket_class ?? matched?.className ?? 'unclassified'}
          {matched && issue.ticket_class && matched.className !== issue.ticket_class && (
            <Tag color="gold" style={{ marginLeft: 6 }}>escalated from {matched.className}</Tag>
          )}
        </Descriptions.Item>
        {matched && (
          <Descriptions.Item label="Criteria">
            {matched.criteria}
          </Descriptions.Item>
        )}
      </Descriptions>
    </Card>
  );
};

// Agent prediction card. Plain operator view of the latest llm-retrieve
// verdict: the class + confidence, whether it agrees with the rule classifier,
// a one-line reason, and the runner-up guesses. Model internals (ensemble arm,
// hold-out, class-10 promotion gate) are intentionally omitted here — they live
// on the Agent Health page. Silently hides when no prediction exists yet.
const AgentPredictionCard: React.FC<{ issueId: number; refreshKey?: number }> = ({
  issueId,
  refreshKey = 0,
}) => {
  const [pred, setPred] = useState<AgentIssuePrediction | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  // UX-041: re-render every 30s so dayjs(...).fromNow() ticks. Without
  // this the "predicted 30s ago" string stuck for the entire time the
  // operator looked at the issue. We don't need to refetch — just nudge
  // a render.
  const [, setNow] = useState<number>(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  // refreshKey is bumped by the parent after a triage write so the card
  // re-fetches and reflects the new ensemble verdict / decided_by /
  // disagreement state. Without this the card displayed the stale prediction
  // until the operator navigated away from the issue.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchAgentIssuePrediction(issueId)
      .then((r) => { if (!cancelled) setPred(r.prediction); })
      .catch(() => { if (!cancelled) setPred(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [issueId, refreshKey]);

  // UX-039: during refetch after a triage write, render a Skeleton instead
  // of `null`. The card was vanishing for 200-1500ms and the surrounding
  // layout jumped. Show a placeholder card only when we previously had a
  // prediction (first-load returns null as before so the card stays hidden
  // for issues that genuinely don't have one yet).
  if (loading && pred) {
    return (
      <Card size="small" styles={{ body: { padding: 12 } }} title="Agent prediction">
        <Skeleton active paragraph={{ rows: 2 }} title={false} />
      </Card>
    );
  }
  if (loading) return null;
  if (!pred) return null;

  const alts = Array.isArray(pred.top_alternatives) ? pred.top_alternatives : [];
  // The one signal an operator needs: does the agent agree with the rule
  // classifier's class, or would it call this something else? Only meaningful
  // when there IS a rule verdict to compare against — rule_class_id can be null
  // (no rule classification), in which case we show no badge rather than
  // falsely claiming "agrees". The deeper mechanics (which ensemble arm
  // decided, hold-out sampling, the class-10 promotion gate, raw-LLM vs
  // surfaced divergence) are debugging detail and live on the Agent Health page.
  const hasRuleVerdict = pred.rule_class_id != null;
  // "disagrees" when the agent's view differs from the rule classifier in EITHER
  // sense: the surfaced class differs from the rule, OR the ensemble surfaced the
  // rule's class over a dissenting raw-LLM verdict (llm_class_id differs from the
  // surfaced class). The second case is the one the removed "LLM wanted X" row
  // used to expose — without it, a rule-surfaced class would falsely read as
  // "agrees" while the model actually dissented.
  const suggestsDifferent = hasRuleVerdict && (
    pred.predicted_class_id !== pred.rule_class_id ||
    (pred.llm_class_id != null && pred.llm_class_id !== pred.predicted_class_id)
  );

  // Translate the internal parse-failure sentinel into plain language — it was
  // leaking into the operator UI where it just looked like a typo.
  const reasoning = pred.reasoning
    ? (pred.reasoning.startsWith('llm-parse-failed')
        ? 'Agent output was unreadable — showing the rule classifier’s verdict instead.'
        : pred.reasoning)
    : null;

  return (
    <Card size="small" styles={{ body: { padding: 12 } }} title={
      <Space size={6}>
        <Typography.Text strong>Agent prediction</Typography.Text>
        {hasRuleVerdict && (
          <Tag color={suggestsDifferent ? 'gold' : 'green'}>
            {suggestsDifferent ? 'disagrees' : 'agrees'}
          </Tag>
        )}
      </Space>
    }>
      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        <div>
          <Tag color="blue" style={{ margin: 0 }}>
            {pred.predicted_class || `cls ${pred.predicted_class_id}`}
          </Tag>
          <Typography.Text type="secondary" style={{ fontSize: 12, marginLeft: 6 }}>
            {pred.confidence}% confident
          </Typography.Text>
        </div>

        {reasoning && (
          <Typography.Paragraph style={{ fontSize: 12, margin: 0, whiteSpace: 'pre-wrap' }}>
            {reasoning}
          </Typography.Paragraph>
        )}

        {alts.length > 0 && (
          <div>
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>Other guesses</Typography.Text>
            <div style={{ marginTop: 4 }}>
              {/* Resolve class ids through classTagMap/classTooltipMap so
                  alternates show "BAT (Battery Drained)" etc. instead of a
                  bare "cls 5" — matches the rest of the app. */}
              {alts.map((a, i) => {
                const meta = classTagMap[a.class_id];
                return (
                  <Tooltip
                    key={`${a.class_id}-${i}`}
                    title={classTooltipMap[a.class_id]}
                  >
                    <Tag color={meta?.color} style={{ marginRight: 4 }}>
                      {meta?.label ?? `cls ${a.class_id}`} · {a.confidence}%
                    </Tag>
                  </Tooltip>
                );
              })}
            </div>
          </div>
        )}

        <Typography.Text type="secondary" style={{ fontSize: 10 }}>
          predicted {dayjs(pred.predicted_at).fromNow()}
        </Typography.Text>
      </Space>
    </Card>
  );
};

interface IssueActionBarProps {
  issue: Issue;
  onAction?: (action: TriageActionDef, comment?: string) => Promise<void>;
  onReclassify?: () => Promise<void>;
  reclassifyLoading?: boolean;
}

const IssueActionBar: React.FC<IssueActionBarProps> = ({ issue, onAction, onReclassify, reclassifyLoading }) => {
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [messageApi, contextHolder] = message.useMessage();

  // Guide the operator toward the action the classifier is pointing at instead
  // of presenting 8 equal choices. The suggested action is the one whose target
  // class matches the issue's current (rule/agent-surfaced) class. Unknown-class
  // issues have no single suggestion — we deliberately highlight nothing rather
  // than fake confidence.
  const suggestedActionKey =
    issue.ticket_class_id != null &&
    issue.ticket_class_id !== TICKET_CLASS_IDS.OFFLINE_UNKNOWN
      ? TRIAGE_ACTIONS.find((a) => a.ticketClassId === issue.ticket_class_id)?.key ?? null
      : null;

  const handle = async (actionDef: TriageActionDef) => {
    if (!onAction) return;
    setActionLoading(actionDef.key);
    try {
      await onAction(actionDef);
      messageApi.success(`Issue #${issue.issue_id} → ${actionDef.label}`);
    } catch (err: any) {
      messageApi.error(`Failed: ${err?.message || 'Unknown error'}`);
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <>
      {contextHolder}
      <div
        style={{
          position: 'sticky',
          bottom: 0,
          padding: '12px 16px',
          background: '#fff',
          borderTop: '1px solid #f0f0f0',
          boxShadow: '0 -4px 12px rgba(0,0,0,0.08)',
          zIndex: 10,
        }}
        role="toolbar"
        aria-label="Issue actions"
        data-tour="action-bar"
      >
        <Space wrap size={8}>
          {TRIAGE_ACTIONS.map((action) => {
            const isSuggested = action.key === suggestedActionKey;
            return (
              <Tooltip
                key={action.key}
                title={`${action.label} [${action.shortcutKey}]${isSuggested ? ' · suggested by classifier' : ''}`}
              >
                <Button
                  icon={actionIcons[action.key]}
                  onClick={() => handle(action)}
                  loading={actionLoading === action.key}
                  aria-label={isSuggested ? `${action.label} (suggested)` : action.label}
                  {...actionButtonStyle[action.key]}
                  style={{
                    fontWeight: 600,
                    ...actionButtonStyle[action.key]?.style,
                    ...(isSuggested
                      ? { boxShadow: '0 0 0 2px #1677ff', borderColor: '#1677ff' }
                      : {}),
                  }}
                >
                  {action.shortLabel}{isSuggested ? ' ★' : ''}
                </Button>
              </Tooltip>
            );
          })}
          <Tooltip title="Re-run the classifier on this issue">
            <Button icon={<ReloadOutlined spin={reclassifyLoading} />} onClick={onReclassify} loading={reclassifyLoading}>
              Reclassify
            </Button>
          </Tooltip>
        </Space>
      </div>
    </>
  );
};

interface IssueDetailPaneProps {
  issue: Issue | null;
  alerts: IssueAlert[];
  alertsLoading: boolean;
  nodeDetail: NodeDetail | null;
  batteryStatus: BatteryStatus | null;
  batteryAtOffline?: BatteryStatus | null;
  gateway: Gateway | null;
  recovery?: NodeRecovery | null;
  nodeHistory: NodeHistoryEvent[];
  firmwareHistory: FirmwareEvent[];
  testJigReports: unknown[];
  locationLogs: unknown[];
  nodeLogEntries: unknown[];
  tasks: Task[];
  loading: boolean;
  mode?: 'triage' | 'issues';
  onAddLocationLog: (text: string) => void;
  onAddNodeLog: (text: string) => void;
  onIssueAction?: (action: TriageActionDef, comment?: string) => Promise<void>;
  onReclassify?: () => Promise<void>;
  reclassifyLoading?: boolean;
  onConfirm?: () => Promise<void>;
  /** Current operator (email/sub) — decides "you" vs a peer on the claim. */
  currentUserEmail?: string | null;
  /** Take over a peer's live claim. Claiming/releasing your own is automatic. */
  onClaim?: (issueId: number, steal?: boolean) => void;
  onTaskCreated: (task: Task) => void;
  onTaskUpdated: (task: Task) => void;
  onTaskDeleted: (taskId: string) => void;
  /** Bumped by the parent (TriagePage) after a triage write so the
   *  AgentPredictionCard re-fetches and shows the post-write verdict. */
  predictionRefreshKey?: number;
}

// Clickable cohort peer chip. Hover shows full peer detail (key,
// position/zone, observable name, class, when its current ticket
// opened); click jumps to the peer's currently-open issue when one
// exists. Critical pieces — key + position — are the room-level
// identifiers operators need to spot patterns ("oh, all of room 401's
// nodes dropped at once").
const CohortPeerChip: React.FC<{
  peer: import('../../config/api').CohortPeer;
  onJump: (issueId: number | null) => void;
}> = ({ peer, onJump }) => {
  const tag = peer.ticket_class_id != null ? classTagMap[peer.ticket_class_id] : null;
  const label =
    peer.key_name ||
    peer.observable_name ||
    peer.observable_id.slice(-6);
  const popContent = (
    <div style={{ maxWidth: 320, fontSize: 12, lineHeight: 1.5 }}>
      <Descriptions size="small" column={1} bordered styles={{ label: { width: 96, fontSize: 11 }, content: { fontSize: 12 } }}>
        <Descriptions.Item label="Key">{peer.key_name || <Typography.Text type="secondary">(unset)</Typography.Text>}</Descriptions.Item>
        <Descriptions.Item label="Position">{peer.position_name || <Typography.Text type="secondary">(unset)</Typography.Text>}</Descriptions.Item>
        <Descriptions.Item label="Node type">
          {peer.node_sub_type
            ? <span>{peer.node_sub_type}{peer.node_type && peer.node_type !== peer.node_sub_type ? ` (${peer.node_type})` : ''}</span>
            : peer.node_type || <Typography.Text type="secondary">(unset)</Typography.Text>}
        </Descriptions.Item>
        <Descriptions.Item label="Node">
          <Typography.Text style={{ fontFamily: 'monospace', fontSize: 11 }}>{peer.observable_id}</Typography.Text>
        </Descriptions.Item>
        <Descriptions.Item label="Class">
          {tag ? <Tag color={tag.color} style={{ margin: 0 }}>{tag.label}</Tag> : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Opened">
          {dayjs(peer.ticket_created).fromNow()} ({dayjs(peer.ticket_created).format('YYYY-MM-DD HH:mm')})
        </Descriptions.Item>
      </Descriptions>
      <div style={{ marginTop: 8, textAlign: 'right' }}>
        {peer.issue_id != null ? (
          <Button size="small" type="primary" onClick={() => onJump(peer.issue_id)}>
            Open this issue →
          </Button>
        ) : (
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            No open issue for this node
          </Typography.Text>
        )}
      </div>
    </div>
  );
  return (
    <Popover content={popContent} placement="top" trigger={['hover', 'click']}>
      <Tag
        color={tag?.color ?? PENDING_CLASS_TAG.color}
        style={{ margin: 0, fontSize: 10, cursor: peer.issue_id != null ? 'pointer' : 'help' }}
        onClick={() => peer.issue_id != null && onJump(peer.issue_id)}
      >
        {label} · {tag?.label ?? PENDING_CLASS_TAG.label}
      </Tag>
    </Popover>
  );
};

const IssueDetailPane: React.FC<IssueDetailPaneProps> = ({
  issue,
  alerts,
  alertsLoading,
  nodeDetail,
  batteryStatus,
  batteryAtOffline,
  gateway,
  recovery,
  nodeHistory,
  firmwareHistory,
  testJigReports,
  locationLogs,
  nodeLogEntries,
  tasks,
  loading,
  mode = 'issues',
  onAddLocationLog,
  onAddNodeLog,
  onIssueAction,
  onReclassify,
  reclassifyLoading,
  onConfirm,
  currentUserEmail,
  onClaim,
  onTaskCreated,
  onTaskUpdated,
  onTaskDeleted,
  predictionRefreshKey,
}) => {
  const isTriage = mode === 'triage';
  const navigate = useNavigate();
  const location = useLocation();
  const [confirming, setConfirming] = useState(false);
  // Ops operators work off the plain-language "Agent prediction" card; the
  // heuristic decision-flow ladder is developer-facing detail, so we hide the
  // "Classifier reasoning" section from them and keep it admin-only (same
  // gating as Agent Health and the other maintainer surfaces).
  const { isAdmin } = useCurrentUser();

  // Jump to a peer node's currently-open issue. Same route as we're on
  // (so triage→triage, issues→issues), with ?issue=<peer issue_id>.
  const jumpToPeerIssue = (peerIssueId: number | null) => {
    if (peerIssueId == null) return;
    const search = new URLSearchParams(window.location.search);
    search.set('issue', String(peerIssueId));
    navigate({ pathname: location.pathname, search: search.toString() });
  };

  // Cohort context — other co-failing nodes at the same location / on
  // the same gateway. Surfaces the "Ah, room 401 + 402 + 403 all dropped
  // → gateway issue" pattern that's hard to spot one issue at a time.
  // Fetched per-issue from /api/issues/:id/cohort.
  const [cohort, setCohort] = useState<CohortResponse | null>(null);
  useEffect(() => {
    if (!issue?.issue_id) {
      setCohort(null);
      return;
    }
    let cancelled = false;
    fetchIssueCohort(issue.issue_id)
      .then((r) => { if (!cancelled) setCohort(r); })
      .catch(() => { if (!cancelled) setCohort(null); });
    return () => { cancelled = true; };
  }, [issue?.issue_id]);
  const [collapsedSections, setCollapsedSections] = useState<string[]>(() => {
    const VERSION_KEY = 'triage-issue-collapsed-version';
    const STATE_KEY = 'triage-issue-collapsed-sections';
    // v3: stop collapsing timeline + firmware by default. Lifecycle events
    // (last mapped/tested/battery-changed) and OTA timing are first-line
    // diagnostic context for offline-unknown nodes; hiding them behind a click
    // meant operators racing the queue missed them. Open by default; the user
    // can still collapse and that choice persists.
    const CURRENT_VERSION = 'v3';
    const DEFAULT_COLLAPSED: string[] = [];
    try {
      if (localStorage.getItem(VERSION_KEY) !== CURRENT_VERSION) {
        localStorage.setItem(STATE_KEY, JSON.stringify(DEFAULT_COLLAPSED));
        localStorage.setItem(VERSION_KEY, CURRENT_VERSION);
        return DEFAULT_COLLAPSED;
      }
      return JSON.parse(localStorage.getItem(STATE_KEY) || '[]');
    } catch {
      return DEFAULT_COLLAPSED;
    }
  });

  const toggleSection = (key: string) => {
    setCollapsedSections((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      localStorage.setItem('triage-issue-collapsed-sections', JSON.stringify(next));
      return next;
    });
  };

  const isSectionOpen = (key: string) => !collapsedSections.includes(key);

  const CollapsibleSection: React.FC<{ title: string; sectionKey: string; icon?: React.ReactNode; children: React.ReactNode }> = ({ title, sectionKey, icon, children }) => {
    const open = isSectionOpen(sectionKey);
    return (
      <div style={{ marginBottom: 16 }}>
        {/* UX-038: keyboard-accessible disclosure. role=button + tabIndex
            + Enter/Space + aria-expanded so screen readers and keyboard
            users can expand sections. The whole header is the hit target
            (same as before for mouse users). */}
        <div
          role="button"
          tabIndex={0}
          aria-expanded={open}
          aria-controls={`section-body-${sectionKey}`}
          onClick={() => toggleSection(sectionKey)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleSection(sectionKey);
            }
          }}
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', marginBottom: open ? 8 : 0, color: '#595959', fontSize: 13, fontWeight: 600, userSelect: 'none' }}
        >
          <span aria-hidden="true" style={{ transform: open ? 'rotate(90deg)' : 'rotate(0)', transition: 'transform 0.2s', display: 'inline-block' }}>&#9654;</span>
          {icon}
          {title}
        </div>
        {open && <div id={`section-body-${sectionKey}`}>{children}</div>}
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ padding: 16 }}>
        <Skeleton active paragraph={{ rows: 2 }} style={{ marginBottom: 16 }} />
        <Skeleton active paragraph={{ rows: 5 }} style={{ marginBottom: 16 }} />
        <Skeleton active paragraph={{ rows: 3 }} />
      </div>
    );
  }

  if (!issue) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Empty description="Select an issue from the list to view details" />
      </div>
    );
  }

  const pseudoTicket = issueToPseudoTicket(issue);
  const classTag = issue.ticket_class_id != null ? classTagMap[issue.ticket_class_id] : null;
  const classTooltip = issue.ticket_class_id != null
    ? classTooltipMap[issue.ticket_class_id] || issue.ticket_class
    : 'Unclassified';
  const conf = confidenceBucket(issue.confidence);

  // Advisory claim state. A claim older than the server TTL is stale, so
  // treat it as unclaimed here too — the operator can re-claim without a steal.
  const claimFresh = issue.claimed_by != null && isClaimFresh(issue.claimed_at);
  const claimedByMe =
    claimFresh && currentUserEmail != null && issue.claimed_by === currentUserEmail;
  const claimedByOther = claimFresh && !claimedByMe;

  // "Looks resolved" recommendation. Auto-resolve is off, so a node that came
  // back and has stayed online a while needs a human to close it — surface
  // that instead of making the operator dig. Signal: recovery says recovered
  // AND it's been >= 4 days since it recovered (stably online, not a blip).
  const RESOLVED_MIN_DAYS = 4;
  const recoveredDays = recovery?.is_recovered && recovery.recovered_date
    ? Math.floor(dayjs().diff(dayjs(recovery.recovered_date), 'day'))
    : null;
  const looksResolved = recoveredDays != null && recoveredDays >= RESOLVED_MIN_DAYS;
  const closeAction = TRIAGE_ACTIONS.find((a) => a.key === 'close') ?? null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div style={{ flex: 1, overflow: 'auto', padding: 16, minWidth: 0 }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={issue.issue_id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            style={{ overflow: 'hidden' }}
          >
            {/* Soft-lock — a peer holds a live claim. Non-blocking: warn so the
                operator doesn't duplicate the investigation, and offer to take
                it over if the peer has actually moved on. */}
            {claimedByOther && (
              <Alert
                type="warning"
                showIcon
                icon={<LockOutlined />}
                style={{ marginBottom: 12 }}
                message={
                  <span>
                    <strong>{shortActor(issue.claimed_by)}</strong> is investigating this issue
                    {issue.claimed_at ? ` (claimed ${dayjs(issue.claimed_at).fromNow()})` : ''}. You may be
                    duplicating work.
                  </span>
                }
                action={
                  onClaim && (
                    <Button size="small" danger onClick={() => onClaim(issue.issue_id, true)}>
                      Take over
                    </Button>
                  )
                }
              />
            )}
            {/* "Looks resolved" — the node recovered and has stayed online a
                while. Auto-resolve is off, so recommend the operator close it.
                Class-bearing issues close cleanly; the server prompts if a
                class is still needed. */}
            {looksResolved && (
              <Alert
                type="success"
                showIcon
                icon={<CheckCircleOutlined />}
                style={{ marginBottom: 12 }}
                message={
                  <span>
                    Looks resolved — <strong>{issue.observable_id}</strong> has been back online{' '}
                    {recoveredDays} days with no new issues.
                  </span>
                }
                action={
                  onIssueAction && closeAction && (
                    <Button
                      size="small"
                      type="primary"
                      onClick={() => { void onIssueAction(closeAction); }}
                    >
                      Close as resolved
                    </Button>
                  )
                }
              />
            )}
            {/* Issue header card */}
            <Card
              size="small"
              style={{ marginBottom: 12 }}
              styles={{ body: { padding: '10px 12px' } }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
                <Typography.Text strong style={{ fontSize: 14, fontFamily: 'monospace' }}>
                  {isTriage ? `Node ${issue.observable_id}` : `Issue #${issue.issue_id}`}
                </Typography.Text>
                <Tooltip title={priorityLabel[issue.priority ?? 3] || `Priority ${issue.priority ?? '?'}`}>
                  {/* Color + text + aria-label — color-blind users / SR users
                      can read the priority without hovering the dot. */}
                  <span
                    role="img"
                    aria-label={priorityLabel[issue.priority ?? 3] || `Priority ${issue.priority ?? '?'}`}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      fontSize: 11,
                      color: '#595959',
                    }}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        display: 'inline-block',
                        width: 10,
                        height: 10,
                        borderRadius: 5,
                        backgroundColor: priorityColor[issue.priority ?? 3] || '#8c8c8c',
                      }}
                    />
                    P{issue.priority ?? '?'}
                  </span>
                </Tooltip>
                <Tooltip title={classTooltip}>
                  {/* UX-025: explicit color for unclassified — central token,
                      not antd's default neutral. */}
                  <Tag
                    color={classTag?.color ?? PENDING_CLASS_TAG.color}
                    style={{ margin: 0 }}
                  >
                    {classTag?.label || PENDING_CLASS_TAG.label}
                  </Tag>
                </Tooltip>
                {conf && (
                  <Tooltip
                    title={
                      <>
                        <div><strong>Classifier:</strong> {issue.classifier_version || 'unknown'}</div>
                        {issue.confidence != null && <div>Confidence: {issue.confidence}</div>}
                        {!isTriage && <div style={{ marginTop: 4, opacity: 0.85 }}>at classify-time (issue already confirmed)</div>}
                      </>
                    }
                  >
                    {/* UX-028: on /issues the issue is already confirmed by
                        a human; a red "lo conf" chip contradicts that. Mute
                        the chip outside triage and re-label so it's clearly
                        historical, not a current warning. */}
                    {isTriage ? (
                      <Tag color={conf.color} style={{ margin: 0 }}>{conf.text} conf</Tag>
                    ) : (
                      <Tag style={{ margin: 0 }}>{conf.text} conf at classify</Tag>
                    )}
                  </Tooltip>
                )}
                <Tag style={{ margin: 0 }}>{alerts.filter((a) => a.removed_at == null).length} / {alerts.length} alerts</Tag>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Opened {dayjs(issue.created_at).fromNow()} · {issue.location_name}
                </Typography.Text>
                {/* Claim is automatic — set on open, cleared on navigating away
                    or on final disposition (server-side). This just reflects
                    that state; take-over for a peer's claim lives in the
                    soft-lock banner above. */}
                {claimedByMe && (
                  <span style={{ marginInlineStart: 'auto' }}>
                    <Tag icon={<LockOutlined />} color="processing" style={{ margin: 0 }}>You're on it</Tag>
                  </span>
                )}
              </div>
              {issue.classifier_notes && (() => {
                // "Why this class" callout: one plain rationale line + an
                // Evidence strip of the concrete signals that drove the class
                // (offline days, battery state, dBm, OTA fails, cohort, …).
                // deriveEvidence surfaces those measurements as chips for EVERY
                // class, so the operator scans the facts instead of parsing a
                // sentence. The prose line stays as the human summary beneath.
                const isUnknown = issue.ticket_class_id === TICKET_CLASS_IDS.OFFLINE_UNKNOWN;
                // Manual override: a human pinned the class, so the displayed
                // class doesn't follow from the rule notes — label the callout
                // accordingly so operators don't read "Stale 112d → battery"
                // and wonder why the class is TAMPERING.
                const isManual = issue.classifier_version === 'manual';
                const parsed = parseClassifierNotes(issue.classifier_notes);
                const evidence = deriveEvidence({ notes: issue.classifier_notes });
                // Sub-class tag is meaningful only when the CURRENT class is
                // OFFLINE_UNKNOWN — issue_predictions is append-only, so the
                // LATERAL-joined unknown_subclass can come from a prior class-10
                // prediction even after the issue has graduated to a named class
                // (e.g. TAMPERING via P4-BATTERY-REMOVED). Showing "fw?" on a
                // class-56 issue reads as a contradiction; hide it.
                const subTag = isUnknown && issue.unknown_subclass
                  ? subclassTagMap[issue.unknown_subclass]
                  : null;
                return (
                  <div
                    data-tour="likely-cause"
                    style={{
                      marginTop: 4,
                      padding: '6px 10px',
                      borderRadius: 6,
                      borderLeft: `3px solid ${isManual ? '#8c8c8c' : isUnknown ? '#faad14' : '#1677ff'}`,
                      background: isManual ? '#fafafa' : isUnknown ? '#fffbe6' : '#f0f7ff',
                    }}
                  >
                    <Typography.Text strong style={{ fontSize: 11, display: 'block', textTransform: 'uppercase', letterSpacing: 0.3, color: isManual ? '#595959' : isUnknown ? '#ad6800' : '#0958d9' }}>
                      {isManual ? 'Manual override — rule said:' : isUnknown ? 'Likely cause — needs confirmation' : 'Why this class'}
                    </Typography.Text>
                    {(evidence.length > 0 || subTag) && (
                      <div style={{ marginTop: 4, marginBottom: 4, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                        {evidence.map((e, idx) => (
                          <Tooltip key={`${e.kind}-${idx}`} title={e.tooltip}>
                            <Tag
                              color={e.color}
                              style={{ margin: 0, fontSize: 10, lineHeight: '16px', padding: '0 4px' }}
                            >
                              {e.label}
                            </Tag>
                          </Tooltip>
                        ))}
                        {subTag && (
                          <Tooltip title={subTag.tooltip}>
                            <Tag
                              color={subTag.color}
                              style={{ margin: 0, fontSize: 10, lineHeight: '16px', padding: '0 4px', opacity: 0.85 }}
                            >
                              {subTag.label}
                            </Tag>
                          </Tooltip>
                        )}
                      </div>
                    )}
                    <Typography.Text style={{ fontSize: 12, color: isManual ? '#8c8c8c' : undefined }}>
                      {parsed.base || issue.classifier_notes}
                    </Typography.Text>
                  </div>
                );
              })()}
              {/* Confirm/Reclassify action row merged into the header card
                  (was a separate "Suggested classification" Card before —
                  duplicated the rationale banner and ate vertical space).
                  Triage-only; on /issues the issue is already confirmed
                  and the action bar at the bottom of the page handles
                  reclassifies. */}
              {isTriage && onConfirm && (() => {
                const c = issue.confidence ?? 0;
                const toneLabel =
                  c >= 70 ? 'High confidence'
                    : c >= 40 ? 'Medium confidence'
                      : 'Low confidence — review carefully';
                return (
                  <div
                    style={{
                      marginTop: 8,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Typography.Text type="secondary" style={{ fontSize: 11, flex: 1, minWidth: 0 }}>
                      Suggested classification · {toneLabel}
                    </Typography.Text>
                    {onReclassify && (
                      <Button
                        size="small"
                        icon={<ReloadOutlined spin={reclassifyLoading} />}
                        loading={reclassifyLoading}
                        onClick={onReclassify}
                      >
                        Reclassify
                      </Button>
                    )}
                    <Button
                      size="small"
                      type="primary"
                      loading={confirming}
                      onClick={async () => {
                        if (!onConfirm) return;
                        setConfirming(true);
                        try { await onConfirm(); } finally { setConfirming(false); }
                      }}
                    >
                      Confirm class
                    </Button>
                  </div>
                );
              })()}
            </Card>

            {/* Cohort banner — when other nodes at the same location or
                on the same gateway are also offline in the last 24h, surface
                the pattern. Gateway cohort is the stronger signal (narrower
                failure scope), so we show it first if present. */}
            {cohort && (cohort.gateway_cohort.length > 0 || cohort.location_cohort.length > 0) && (
              <Card
                size="small"
                style={{
                  marginBottom: 12,
                  border: '1px solid #91d5ff',
                  background: '#e6f7ff',
                }}
                styles={{ body: { padding: '10px 14px' } }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <Typography.Text strong style={{ fontSize: 13 }}>
                    Cohort context — this node isn't failing alone
                  </Typography.Text>
                  {cohort.gateway_cohort.length > 0 && (
                    <div style={{ fontSize: 12 }}>
                      <Typography.Text strong>{cohort.gateway_cohort.length}</Typography.Text>
                      {' other node'}{cohort.gateway_cohort.length === 1 ? '' : 's'}
                      {' on the same gateway also offline (last 24h).'}
                      {cohort.gateway_cohort.length >= 3 && (
                        <Typography.Text strong style={{ color: '#d4380d' }}>
                          {' '}Check the gateway first — this is gateway-side, not per-node.
                        </Typography.Text>
                      )}
                      <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {cohort.gateway_cohort.slice(0, 10).map((peer) => (
                          <CohortPeerChip
                            key={peer.observable_id}
                            peer={peer}
                            onJump={jumpToPeerIssue}
                          />
                        ))}
                        {cohort.gateway_cohort.length > 10 && (
                          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                            +{cohort.gateway_cohort.length - 10} more
                          </Typography.Text>
                        )}
                      </div>
                    </div>
                  )}
                  {cohort.location_cohort.length > 0 && (
                    <div style={{ fontSize: 12 }}>
                      <Typography.Text strong>{cohort.location_cohort.length}</Typography.Text>
                      {' other node'}{cohort.location_cohort.length === 1 ? '' : 's'}
                      {' at this location offline (last 24h)'}
                      {cohort.gateway_cohort.length > 0 && ' — includes the gateway cohort above'}
                      {'.'}
                      {cohort.location_cohort.length >= 5 && cohort.gateway_cohort.length < 3 && (
                        <Typography.Text strong style={{ color: '#d46b08' }}>
                          {' '}Location-wide event — check power / network.
                        </Typography.Text>
                      )}
                      <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {cohort.location_cohort.slice(0, 12).map((peer) => (
                          <CohortPeerChip
                            key={peer.observable_id}
                            peer={peer}
                            onJump={jumpToPeerIssue}
                          />
                        ))}
                        {cohort.location_cohort.length > 12 && (
                          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                            +{cohort.location_cohort.length - 12} more
                          </Typography.Text>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            )}

            {/* Confirm/Reclassify action row was previously a standalone
                "Suggested classification" Card here — merged into the header
                card's "Why this class" banner above to reduce vertical stack. */}

            {/* Classifier reasoning — which heuristic matched + full ladder.
                Admin-only: ops operators use the plain-language Agent
                prediction card below instead of the heuristic ladder. */}
            {isAdmin && (
              <CollapsibleSection title="Classifier reasoning" sectionKey="classifier">
                <ClassifierSection issue={issue} />
              </CollapsibleSection>
            )}

            {/* Triage agent's view — shows when the LLM-retrieve sidecar has
                produced a prediction for this issue. Silent until then. */}
            <AgentPredictionCard issueId={issue.issue_id} refreshKey={predictionRefreshKey} />

            <NodeInfoBar
              ticket={pseudoTicket}
              nodeDetail={nodeDetail}
              batteryStatus={batteryStatus}
              nodeHistory={nodeHistory}
              recovery={recovery}
              meteredAssets={issue.metered_assets}
            />

            <SuggestedActionBanner
              batteryStatus={batteryStatus}
              ticket={pseudoTicket}
              nodeDetail={nodeDetail}
              onAction={onIssueAction}
            />

            <CollapsibleSection title={`Alerts (${alerts.length})`} sectionKey="alerts" icon={<HistoryOutlined />}>
              <Card size="small" styles={{ body: { padding: 0 } }}>
                {(() => {
                  // The Alert "Class" column came from the upstream alerts
                  // service; the Issue's class comes from the rule classifier.
                  // They usually match, in which case the column is noise.
                  // Show it only when at least one alert disagrees with the
                  // parent issue — that's the interesting case (e.g. issue
                  // grouped as TAMP but underlying alerts are HW + UNK).
                  const issueClassId = issue.ticket_class_id;
                  const hasDivergence = alerts.some(
                    (a) => a.ticket_class_id !== issueClassId,
                  );
                  return (
                <Table
                  dataSource={alerts}
                  rowKey="ticket_id"
                  size="small"
                  loading={alertsLoading}
                  pagination={false}
                  scroll={{ x: 'max-content', y: 260 }}
                  locale={{ emptyText: 'No linked alerts' }}
                  columns={[
                    {
                      title: 'Alert',
                      dataIndex: 'ticket_id',
                      width: 90,
                      render: (id: number) => (
                        <Typography.Text style={{ fontFamily: 'monospace', fontSize: 12 }}>#{id}</Typography.Text>
                      ),
                    },
                    ...(hasDivergence
                      ? [{
                          title: 'Alert class',
                          dataIndex: 'ticket_class_id',
                          width: 110,
                          render: (cid: number) => {
                            const tag = classTagMap[cid];
                            return tag ? <Tag color={tag.color} style={{ margin: 0 }}>{tag.label}</Tag> : cid;
                          },
                        }]
                      : []),
                    {
                      title: 'Status',
                      dataIndex: 'status_name',
                      width: 110,
                      render: (n: string, row: IssueAlert) => (
                        <Tag style={{ margin: 0 }} color={row.removed_at ? 'default' : 'blue'}>
                          {n || row.ticket_status_id}
                        </Tag>
                      ),
                    },
                    {
                      title: 'Created',
                      dataIndex: 'ticket_created',
                      width: 120,
                      render: (d: string) => (
                        <Tooltip title={dayjs(d).format('YYYY-MM-DD HH:mm')}>
                          <span style={{ fontSize: 12 }}>{dayjs(d).fromNow()}</span>
                        </Tooltip>
                      ),
                    },
                    {
                      // Was two columns: "Added" + "Removed". Operators read
                      // them as three different timestamps next to Created and
                      // a green "active" word in the Removed column that
                      // looked like a success state. Collapsed into one
                      // "Linked" column: shows when the alert was attached
                      // to this issue, plus "(unlinked Yago)" if it later
                      // dropped out. Active links read as just the added date.
                      title: 'Linked',
                      dataIndex: 'added_at',
                      render: (added: string, row: IssueAlert) => {
                        const removed = row.removed_at;
                        return (
                          <Tooltip title={`added ${dayjs(added).format('YYYY-MM-DD HH:mm')}${removed ? ` · unlinked ${dayjs(removed).format('YYYY-MM-DD HH:mm')}` : ''}`}>
                            <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                              {dayjs(added).fromNow()}
                              {removed && <> · unlinked {dayjs(removed).fromNow()}</>}
                            </span>
                          </Tooltip>
                        );
                      },
                    },
                  ]}
                />
                  );
                })()}
              </Card>
            </CollapsibleSection>

            {!isTriage && (
              <>
                <CollapsibleSection title={`Tasks (${tasks.length})`} sectionKey="tasks">
                  <TaskSection
                    ticket={pseudoTicket}
                    issueId={issue.issue_id}
                    tasks={tasks}
                    onTaskCreated={onTaskCreated}
                    onTaskUpdated={onTaskUpdated}
                    onTaskDeleted={onTaskDeleted}
                  />
                </CollapsibleSection>

                <CollapsibleSection title="Task history" sectionKey="task-history">
                  <Card size="small" styles={{ body: { padding: '12px 16px' } }}>
                    <TaskTimeline tasks={tasks} />
                  </Card>
                </CollapsibleSection>

                {/* Who-did-what audit. Un-gated 2026-07-10 — operators need to
                    see an issue was already triaged (and by whom) to avoid
                    duplicating work. The section's children (and thus the
                    fetch) mount only on expand. */}
                <CollapsibleSection title="Triage history" sectionKey="triage-history" icon={<HistoryOutlined />}>
                  <Card size="small" styles={{ body: { padding: '12px 16px' } }}>
                    <TriageHistory issueId={issue.issue_id} />
                  </Card>
                </CollapsibleSection>
              </>
            )}

            <CollapsibleSection title="Info" sectionKey="info">
              <InfoCards
                ticket={pseudoTicket}
                nodeDetail={nodeDetail}
                batteryStatus={batteryStatus}
                batteryAtOffline={batteryAtOffline}
                gateway={gateway}
              />
            </CollapsibleSection>

            <CollapsibleSection title="Investigation Charts" sectionKey="charts">
              <Suspense fallback={<Skeleton.Node active style={{ width: '100%', height: 200 }} />}>
                <InvestigationDashboard
                  nodeMacId={issue.observable_id}
                  nodeHistory={nodeHistory}
                  firmwareHistory={firmwareHistory}
                />
              </Suspense>
            </CollapsibleSection>

            <CollapsibleSection title="Node Life Timeline" sectionKey="timeline">
              <NodeLifeTimeline
                events={nodeHistory}
                testJigReports={testJigReports}
                currentTicketId={-issue.issue_id}
              />
            </CollapsibleSection>

            <CollapsibleSection title="Logs" sectionKey="logs">
              <LogsSection
                locationLogs={locationLogs}
                nodeLogs={nodeLogEntries}
                onAddLocationLog={onAddLocationLog}
                onAddNodeLog={onAddNodeLog}
              />
            </CollapsibleSection>

            <CollapsibleSection title="Firmware History" sectionKey="firmware">
              <FirmwareHistoryTable firmwareHistory={firmwareHistory} />
            </CollapsibleSection>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* ActionBar renders on BOTH /triage and /issues. On /triage, picking
          any action (Hardware, Tampering, etc.) implicitly confirms the
          issue — server stamps classifier_version='manual' on the triage
          POST. Previously this bar was hidden on /triage, forcing the
          operator to click Confirm first → land on /issues → then apply
          the action. Two steps for what should be one click. */}
      <IssueActionBar
        issue={issue}
        onAction={onIssueAction}
        onReclassify={onReclassify}
        reclassifyLoading={reclassifyLoading}
      />
    </div>
  );
};

export default IssueDetailPane;
